import React from "react";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import Pagination from "../../shared/Pagination";
import useClientPagination from "../../../hooks/useClientPagination";
import { getCiReportCompetitiveRanking } from "../../../api/competitiveIntelligence";
import { InferredOccupancy } from "../../../utils/ciFormat";
import ThinScroll from "../../shared/ThinScroll";
import { PanelTakeaway, fmtRate, fmtPct } from "../shared";

const PAGE_SIZE = 15;

// Inline price-range bar showing min → avg → max for one row, scaled across the
// global min/max of the dataset so rows are comparable.
const PriceRangeBar = ({ min, avg, max, globalMin, globalMax }) => {
  if (min == null || max == null || globalMax === globalMin) return null;
  const range = globalMax - globalMin;
  const left = ((min - globalMin) / range) * 100;
  const width = ((max - min) / range) * 100;
  const avgPct = avg == null ? null : ((avg - globalMin) / range) * 100;
  return (
    <div className="relative w-32 h-1.5 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden">
      <div
        className="absolute h-full bg-[#32B1FF]/40"
        style={{ left: `${left}%`, width: `${Math.max(width, 1)}%` }}
      />
      {avgPct != null && (
        <div
          className="absolute top-0 h-full w-0.5 bg-[#32B1FF]"
          style={{ left: `${avgPct}%` }}
        />
      )}
    </div>
  );
};

const CompetitiveRankingBody = ({ rows }) => {
  if (!rows.length) {
    return <PanelEmpty message="No ranking data for the selected category." />;
  }

  const globalMin = Math.min(...rows.map((row) => row.min_price ?? Infinity));
  const globalMax = Math.max(...rows.map((row) => row.max_price ?? -Infinity));

  // Derive the legend from the data so it stays accurate if the backend flips
  // its rank convention. Compare the two cheapest avg_prices that have ranks
  // and check whether the lower rank has the higher or lower price.
  const ranked = rows
    .filter((row) => row.price_rank != null && row.avg_price != null)
    .sort((rowA, rowB) => rowA.price_rank - rowB.price_rank);
  let rankLegend = null;
  if (ranked.length >= 2) {
    const first = ranked[0];
    const next = ranked.find((row) => row.avg_price !== first.avg_price);
    if (next) {
      rankLegend = first.avg_price > next.avg_price
        ? "Rank 1 = highest average price."
        : "Rank 1 = lowest average price.";
    }
  }

  return (
    <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
      <table className="w-full text-sm responsive-card-table">
        <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <th className="px-3 py-2.5 text-left font-semibold">Rank</th>
            <th className="px-3 py-2.5 text-left font-semibold">Property</th>
            <th className="px-3 py-2.5 text-right font-semibold">Avg Rate</th>
            <th className="px-3 py-2.5 text-left font-semibold">Range</th>
            <th className="px-3 py-2.5 text-right font-semibold">Min</th>
            <th className="px-3 py-2.5 text-right font-semibold">Max</th>
            <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Limited Avail. Days</th>
            <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Limited Avail. %</th>
            <th className="px-3 py-2.5 text-right font-semibold">Avg Occupancy</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={`${row.price_rank || row.property_name || index}`}
              className={`border-b dark:border-gray-800 transition ${
                row.is_client
                  ? "bg-[#32B1FF]/5 dark:bg-[#32B1FF]/10 hover:bg-[#32B1FF]/10"
                  : "hover:bg-gray-50 dark:hover:bg-gray-800/50"
              }`}
            >
              <td data-label="Rank" className="px-3 py-2.5 whitespace-nowrap">
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                  row.is_client
                    ? "bg-[#32B1FF] text-white"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200"
                }`}>
                  {row.price_rank == null ? "—" : row.price_rank + 1}
                </span>
              </td>
              <td data-label="Property" className={`px-3 py-2.5 whitespace-nowrap ${row.is_client ? "font-semibold text-[#32B1FF]" : "text-gray-900 dark:text-gray-100"}`}>
                {row.is_client ? `${row.property_name === "YOU" ? "YOU" : `${row.property_name} (YOU)`}` : (row.property_name || "—")}
              </td>
              <td data-label="Avg Rate" className={`px-3 py-2.5 text-right font-semibold whitespace-nowrap ${row.is_client ? "text-[#32B1FF]" : "text-gray-900 dark:text-gray-100"}`}>
                {fmtRate(row.avg_price)}
              </td>
              <td data-label="Range" className="px-3 py-2.5 whitespace-nowrap">
                <PriceRangeBar
                  min={row.min_price}
                  avg={row.avg_price}
                  max={row.max_price}
                  globalMin={globalMin}
                  globalMax={globalMax}
                />
              </td>
              <td data-label="Min" className="px-3 py-2.5 text-right text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmtRate(row.min_price)}</td>
              <td data-label="Max" className="px-3 py-2.5 text-right text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmtRate(row.max_price)}</td>
              <td data-label="Limited Avail. Days" className="px-3 py-2.5 text-right font-semibold text-amber-600 dark:text-amber-400 whitespace-nowrap">{row.soldout_dates ?? 0}</td>
              <td data-label="Limited Avail. %" className="px-3 py-2.5 text-right font-semibold text-amber-600 dark:text-amber-400 whitespace-nowrap">{fmtPct(row.soldout_pct)}</td>
              <td data-label="Avg Occupancy" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap"><InferredOccupancy value={row.avg_occupancy} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-4 py-2 text-[11px] text-gray-500 dark:text-gray-400">
        {rankLegend && <>{rankLegend} </>}Bar shows each property's price range across all tracked dates.
      </div>
    </ThinScroll>
  );
};

const CompetitiveRankingBodyPaginated = ({ rows }) => {
  const { page, setPage, pageItems, totalPages, totalItems } = useClientPagination(rows, { pageSize: PAGE_SIZE });
  return (
    <>
      <CompetitiveRankingBody rows={pageItems} />
      {totalPages > 1 && (
        <div className="px-5 pt-1 pb-3 border-t border-gray-200 dark:border-gray-800">
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={totalItems}
            perPage={PAGE_SIZE}
            label="properties"
          />
        </div>
      )}
    </>
  );
};

const CompetitiveRankingPanel = ({ clientId, categoryCode, say }) => (
  <AnalysisPanel
    id={`competitive-ranking-${clientId}-${categoryCode || "all"}`}
    title="Competitive Ranking"
    tip="Ranks you and each competitor by average rate, plus how often each is at limited availability. Useful for seeing whether you're priced like the cheapest, mid-tier, or premium property in the set."
    description="Where you sit in the market by price, with limited-availability frequency and price range per competitor."
    badge={categoryCode ? categoryCode.toUpperCase() : null}
    fetcher={() => getCiReportCompetitiveRanking(clientId, { categoryCode: categoryCode || "rv" })}
    deps={[clientId, categoryCode]}
  >
    {({ data }) => {
      const rows = Array.isArray(data) ? [...data].sort((rowA, rowB) => (rowA.price_rank ?? 0) - (rowB.price_rank ?? 0)) : [];
      return (
        <>
          <PanelTakeaway panelKey="competitive_ranking" say={say} />
          <CompetitiveRankingBodyPaginated rows={rows} />
        </>
      );
    }}
  </AnalysisPanel>
);

export default CompetitiveRankingPanel;
